import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { useAdminTrpc } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'

export function WalletSection({ userId }: { userId: string }) {
	const trpc = useAdminTrpc()
	const queryClient = useQueryClient()
	const [amount, setAmount] = useState('')
	const [note, setNote] = useState('')
	// Idempotency-key policy (mirrors PurchasePanel.tsx): rotate the key only
	// after a successful adjust or after INSUFFICIENT_FUNDS — that outcome is
	// terminal, so replaying it would just repeat the same error. Keep the key
	// on any other error so a re-click after a lost response replays the
	// original attempt instead of moving money twice.
	const [idemKey, setIdemKey] = useState(() => crypto.randomUUID())
	const { t, prefixT } = useTranslate('pages.adminClients')

	const { data, isLoading } = useQuery(trpc.wallets.GetByUser.queryOptions({ userId }))
	const adjust = useMutation(trpc.wallets.Adjust.mutationOptions())
	const walletTransactionLabels: Record<string, string> = {
		topup: t('status.walletTransaction.topup'),
		purchase: t('status.walletTransaction.purchase'),
		refund: t('status.walletTransaction.refund'),
		bonus: t('status.walletTransaction.bonus'),
		adjustment: t('status.walletTransaction.adjustment'),
	}

	async function handleAdjust() {
		const parsed = Number(amount.replace(/[^\d-]/g, ''))
		if (!parsed || note.trim().length < 3) {
			toast.error(prefixT('adjustmentInvalid'))
			return
		}
		try {
			const res = await adjust.mutateAsync({
				userId,
				amountUzs: parsed,
				note: note.trim(),
				idempotencyKey: idemKey,
			})
			toast.success(prefixT('adjustmentSuccess', { balance: t('common.amountUzs', { amount: formatUzsAmount(res.balanceAfterUzs) }) }))
			setAmount('')
			setNote('')
			setIdemKey(crypto.randomUUID())
		} catch (e) {
			if (e instanceof Error && e.message.includes('INSUFFICIENT_FUNDS')) {
				toast.error(prefixT('insufficientFunds'))
				setIdemKey(crypto.randomUUID())
			} else {
				toast.error(prefixT('adjustmentFailed'))
			}
		}
		void queryClient.invalidateQueries()
	}

	if (isLoading || !data) return null

	return (
		<section className='mt-4 rounded-xl border p-3'>
			<div className='mb-2 flex items-baseline justify-between'>
				<h2 className='font-semibold'>{prefixT('wallet')}</h2>
				<div className='text-lg font-bold'>{t('common.amountUzs', { amount: formatUzsAmount(data.balanceUzs) })}</div>
			</div>
			{data.driftUzs !== null && data.driftUzs !== 0 && (
				<div className='mb-2 rounded bg-red-100 p-2 text-sm text-red-700'>
					{prefixT('balanceDrift', { amount: t('common.amountUzs', { amount: formatUzsAmount(data.driftUzs) }) })}
				</div>
			)}
			<div className='mb-3 flex items-center gap-2'>
				<Input className='w-32' placeholder={prefixT('adjustmentAmount')} value={amount} onChange={(e) => setAmount(e.target.value)} />
				<Input
					className='flex-1'
					placeholder={prefixT('adjustmentReason')}
					value={note}
					onChange={(e) => setNote(e.target.value)}
				/>
				<Button size='sm' disabled={adjust.isPending} onClick={handleAdjust}>
					{prefixT('applyAdjustment')}
				</Button>
			</div>
			<div className='space-y-1 text-sm'>
			{data.transactions.map((tx) => {
					const type = walletTransactionLabels[tx.type] ?? tx.type
					const label = tx.note
						? prefixT('transactionWithNote', { type, note: tx.note })
						: type
					return (
						<div
							key={tx._id}
							className='flex justify-between border-b py-1 last:border-0'
						>
							<span>
								{label}
							</span>
							<span className={tx.amountUzs > 0 ? 'text-green-600' : ''}>
								{tx.amountUzs > 0 ? '+' : ''}
								{t('common.amountUzs', { amount: formatUzsAmount(tx.amountUzs) })}
							</span>
						</div>
					)
				})}
				{data.transactions.length === 0 && <div className='text-muted-foreground'>{prefixT('noTransactions')}</div>}
			</div>
		</section>
	)
}
